import i18n from '@/utils/i18n';
import React from 'react';
import { ArrowDirection } from '@sales-portal/domain/pricing/screens/management/types';
import { createStyles } from '@/contexts/theme-context';
import {
  formatCompactCurrency,
  formatPercentage,
} from '@sales-portal/blocs/utils/money';
import {
  getDeltaAttributes,
  resolveDelta,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/table-ui';
import { Typography, TypographyVariant } from '@usepepper/ui/atoms/typography';
import { usePricingSummaryHeaderLayout } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-pricing-summary-header-layout';
import { View } from 'react-native';

interface Props {
  avgMarginPercentage: number | null;
  currencyCode: string;
  customerCount: number;
  totalEditsImpactInMicros: number;
  totalGrossProfitInMicros: number;
  totalRevenueInMicros: number;
}

const SummaryItem: React.FC<{
  deltaDirection?: ArrowDirection;
  headingVariant: TypographyVariant;
  label: string;
  value: string;
}> = ({ deltaDirection, headingVariant, label, value }) => {
  const [styles, theme] = useStyles();
  const { color, Icon } = getDeltaAttributes(deltaDirection, theme);
  return (
    <View style={styles.summaryItem}>
      <View style={styles.summaryValue}>
        <Typography variant={headingVariant}>{value}</Typography>
        {Icon ? (
          <Icon fill={color} height={12} stroke={color} width={12} />
        ) : null}
      </View>
      <Typography color={theme.sk.color.text.primary} variant="paragraph-md">
        {label}
      </Typography>
    </View>
  );
};

export const PricingCustomersSummaryHeader: React.FC<Props> = ({
  avgMarginPercentage,
  currencyCode,
  customerCount,
  totalEditsImpactInMicros,
  totalGrossProfitInMicros,
  totalRevenueInMicros,
}) => {
  const [styles] = useStyles();
  const {
    containerStyle,
    handleLayout,
    headingVariant,
    isStacked,
    wrapperStyle,
  } = usePricingSummaryHeaderLayout();

  const { direction, formattedValue } = resolveDelta(
    totalEditsImpactInMicros,
    (v) => formatCompactCurrency(v, currencyCode),
  );

  const items = (
    <>
      <SummaryItem
        headingVariant={headingVariant}
        label={i18n.t('pricing.customers.sidenav_title')}
        value={String(customerCount)}
      />
      <SummaryItem
        headingVariant={headingVariant}
        label={i18n.t('pricing.table.header.weekly_revenue')}
        value={formatCompactCurrency(totalRevenueInMicros, currencyCode)}
      />
      <SummaryItem
        headingVariant={headingVariant}
        label={i18n.t('pricing.table.header.weekly_gross_profit')}
        value={formatCompactCurrency(totalGrossProfitInMicros, currencyCode)}
      />
      <SummaryItem
        headingVariant={headingVariant}
        label={i18n.t('pricing.table.header.realized_gross_margin')}
        value={
          avgMarginPercentage === null
            ? '--'
            : formatPercentage(avgMarginPercentage, 1, 1)
        }
      />
      <SummaryItem
        deltaDirection={direction}
        headingVariant={headingVariant}
        label={i18n.t('pricing.table.header.weekly_edits_impact')}
        value={formattedValue}
      />
    </>
  );

  return (
    <View onLayout={handleLayout} style={containerStyle}>
      <View style={wrapperStyle}>
        <View collapsable={false}>
          <Typography variant="heading-lg">
            {i18n.t('pricing.customers.sidenav_title')}
          </Typography>
        </View>
        {isStacked ? (
          <View style={styles.stackedSummaryItems}>{items}</View>
        ) : (
          items
        )}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  stackedSummaryItems: {
    columnGap: theme.sk.spacing.spaceXlarge,
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: theme.sk.spacing.spaceLarge,
  },
  summaryItem: {
    alignItems: 'center',
    gap: theme.sk.spacing.spaceXsmall,
  },
  summaryValue: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
}));
